import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ page = 1, perPage = 10 }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/notifications?page=${page}&per_page=${perPage}`);
      console.log('Notifications response:', response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const markNotificationsAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationIds, { rejectWithValue }) => {
    try {
      await api.patch('/api/notifications', { notification_ids: notificationIds });
      return notificationIds;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteNotifications = createAsyncThunk(
  'notifications/deleteNotifications',
  async (notificationIds, { dispatch, rejectWithValue }) => {
    try {
      await api.delete('/api/notifications', { 
        data: { notification_ids: notificationIds } 
      });
      
      // After successful deletion, refresh the current page
      // to ensure proper pagination
      dispatch(fetchNotifications({ page: 1 }));
      
      return notificationIds;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete notifications');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/notifications/unread-count');
      return response.data.unread_count;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch unread count');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    status: 'idle',
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    unreadCount: 0,
    processedIds: [],
  },
  reducers: {
    addNotification: (state, action) => {
      const newNotification = action.payload;
      const existingIndex = state.items.findIndex(item => item.id === newNotification.id);
      if (existingIndex === -1) {
        state.items.unshift(newNotification);
        state.totalItems += 1;
        if (!newNotification.read) {
          state.unreadCount += 1;
        }
      }
    },
    addNotifications: (state, action) => {
      const newNotifications = Array.isArray(action.payload) ? action.payload : [action.payload];
      const uniqueNewNotifications = newNotifications.filter(newNotif => {
        if (state.processedIds.includes(newNotif.id)) {
          return false; // Skip if already processed
        }
        state.processedIds.push(newNotif.id);
        return !state.items.some(existingNotif => existingNotif.id === newNotif.id);
      });
      state.items.unshift(...uniqueNewNotifications);
      state.totalItems += uniqueNewNotifications.length;
      state.unreadCount += uniqueNewNotifications.filter(n => !n.read).length;
    },
    updateUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    clearProcessedIds: (state) => {
      state.processedIds = [];
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.notifications;
        state.currentPage = action.payload.page;
        state.totalPages = action.payload.pages;
        state.totalItems = action.payload.total;
        // Update unreadCount only if provided in the API response
        if (action.payload.unreadCount !== undefined) {
          state.unreadCount = action.payload.unreadCount;
        }
        // Clear processed IDs when fetching new notifications
        state.processedIds = [];
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(markNotificationsAsRead.fulfilled, (state, action) => {
        let markedAsReadCount = 0;
        state.items.forEach(notification => {
          if (action.payload.includes(notification.id) && !notification.read) {
            notification.read = true;
            markedAsReadCount++;
          }
        });
        state.unreadCount = Math.max(0, state.unreadCount - markedAsReadCount);
      })
      .addCase(deleteNotifications.fulfilled, (state, action) => {
        // Remove deleted notifications from the current state
        state.items = state.items.filter(
          notification => !action.payload.includes(notification.id)
        );
        
        // Update counts
        state.totalItems = Math.max(0, state.totalItems - action.payload.length);
        state.unreadCount = state.items.filter(n => !n.read).length;
        
        // If we've deleted all items on the current page (except the last page),
        // we might want to adjust the current page
        if (state.items.length === 0 && state.currentPage > 1) {
          state.currentPage = Math.max(1, state.currentPage - 1);
        }
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });
  },
});

export const { addNotification, addNotifications, updateUnreadCount, clearProcessedIds, setUnreadCount } = notificationSlice.actions;

export default notificationSlice.reducer;
